import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, ExternalLink, Sparkles, BookOpen, ListTree } from "lucide-react";
import Seo from "@/components/Seo";
import { SITE_ORIGIN } from "@/config";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuideDetail() {
  const { slug } = useParams();
  const { lang, t, dir } = useLang();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setError(false);
    axios.get(`${API}/guides/${slug}?lang=${lang}`)
      .then(({ data }) => { if (active) setD(data); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug, lang]);

  if (loading) return <div className="es-page es-state" data-testid="guide-detail-loading"><Loader2 className="es-spin" size={22} /> {t("common.loading")}</div>;
  if (error || !d) return <div className="es-page es-state" data-testid="guide-detail-error">{t("common.noResults")} <Link className="es-inline-link" to="/guides">{t("common.back")}</Link></div>;

  const askAi = () => navigate(`/?q=${encodeURIComponent(`Summarize the Evony guide: ${d.englishTitle || d.title}`)}`);
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Article",
    headline: d.title, articleSection: d.category || undefined,
    about: "Evony: The King's Return",
    dateModified: d.updatedAt || undefined,
    mainEntityOfPage: `${SITE_ORIGIN}/guides/${d.slug}`,
  };

  return (
    <div className="es-page es-detail" data-testid="page-guide-detail" dir={dir}>
      <Seo title={`${d.title} | Evony Strategist`}
        description={(d.summary && d.summary.slice(0, 155)) || `${d.title} — an Evony strategy guide, sourced from the Evony Guide Wiki.`}
        path={`/guides/${d.slug}`} jsonLd={jsonLd} />

      <Link to="/guides" className="es-back" data-testid="guide-back"><ArrowLeft size={16} /> {t("guides.title")}</Link>

      <div className="es-detail-head">
        <div className="es-detail-icon"><BookOpen size={26} /></div>
        <div>
          {d.category && <div className="es-guide-cat" data-testid="guide-category">{d.category}</div>}
          <h1 className="es-h1" data-testid="guide-title">{d.title}</h1>
        </div>
      </div>

      {d.summary && <p className="es-guide-summary" data-testid="guide-summary">{d.summary}</p>}

      <div className="es-detail-actions">
        <button className="es-btn-primary es-askai" data-testid="guide-askai" onClick={askAi}>
          <Sparkles size={16} /> {t("common.askAi")}
        </button>
        <a className="es-btn-secondary" href={d.sourceUrl} target="_blank" rel="noreferrer" data-testid="guide-wiki-link">
          {t("guides.openWiki")} <ExternalLink size={14} />
        </a>
      </div>

      {d.headings?.length > 0 && (
        <section className="es-detail-section" data-testid="guide-contents">
          <h2 className="es-h2"><ListTree size={18} /> {t("guides.contents")}</h2>
          <ul className="es-toc">
            {d.headings.map((h, i) => (
              <li key={i} className={`es-toc-item lvl${h.level}`}>{h.heading}</li>
            ))}
          </ul>
          <p className="es-note" data-testid="guide-content-note">{t("guides.contentNote")}</p>
        </section>
      )}

      {d.availableLanguages?.length > 0 && (
        <section className="es-detail-section" data-testid="guide-languages">
          <h2 className="es-h2">{t("guides.availableIn")}</h2>
          <div className="es-badges">
            {d.languages.map((l) => (
              l.sourceUrl
                ? <a key={l.language} className="es-badge es-badge-link" href={l.sourceUrl} target="_blank" rel="noreferrer">{l.language}</a>
                : <span key={l.language} className="es-badge">{l.language}</span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
